// Authenticated self-service enrollment completion wizard — Auth0-first mode
// (Task 3). Route `/my/enroll` (role 'user'). Unlike the public
// `/enroll/:token` wizard (`EnrollPage.tsx`, contractor OTP path — DO NOT
// modify), the caller here already has a valid Auth0 session: identity
// (name/email) comes straight from `useAuth0().user`, there is no
// email/OTP step at all, and the hard gate is simply "is the Auth0 email
// verified" (`user.email_verified`) — the API would 403 EMAIL_NOT_VERIFIED
// regardless, this just avoids a doomed round-trip. The ID token may lack the
// claim, so only an explicit `false` blocks up-front; otherwise the API's
// EMAIL_NOT_VERIFIED 403 (load or any submit) switches to the same screen.
//
// Steps, driven by the `GET /api/enroll/me` completeness report
// (`missing`): the report does NOT return the coworker's current field
// values (see `MyEnrollmentCompleteness` in api/enrollMe.ts — only
// `status`/`missing`), so "prefill" here means deciding which steps to
// skip, not filling in prior answers.
//   - 'profile': mobilePhone/taxIdCountry/taxIdType/taxIdValue/birthDate,
//     shown when `missing` includes 'mobilePhone' or 'taxIdValue'.
//   - 'photo': guided camera capture only (no file upload), shown when
//     `missing` includes 'facePhoto'; advances to 'confirm' once a photo is
//     obtained.
//   - 'confirm': photo preview + LGPD consent + submit — split from 'photo'
//     so "take photo" and "submit" are never on the same screen.
//   - 'finish': explicit "complete enrollment" action -> POST .../finalize.
//   - 'done': terminal screen, approved vs pending-approval per response.
//
// Retake mode (`?retake=1`, linked from the home banner "the door can't
// recognise you"): regardless of `missing`, the wizard is just photo ->
// confirm -> upload + finalize, then a "new photo sent" done state.
import { useAuth0 } from '@auth0/auth0-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Banner, Breadcrumbs, Button, Empty, Input, Loader, Text } from '@cloudflare/kumo'
import { CheckCircleIcon, HourglassMediumIcon, HouseIcon, UserIcon } from '@phosphor-icons/react'
import {
  formatPhoneAsYouType,
  individualTaxIdTypesForCountry,
  isValidMobilePhone,
  normalizeTaxId,
  toE164,
  validateTaxId,
} from '@hub-wifi/shared'
import type { EnrollmentStatus } from '@hub-wifi/shared'
import { ApiError } from '../api/client'
import { putMyFacePhoto } from '../api/faceStatus'
import { resendMyVerification } from '../api/verification'
import type { MyFaceStatus } from '@hub-wifi/shared'
import {
  finalizeMyEnroll,
  getMyEnrollment,
  postMyEnrollFace,
  putMyEnrollProfile,
  type MyEnrollmentCompleteness,
} from '../api/enrollMe'
import { PageHeader } from '../components/kumo/page-header/page-header'
import { EnrollDone } from '../components/enroll/EnrollDone'
import { EnrollProgress } from '../components/enroll/EnrollProgress'
import { PhotoConfirm } from '../components/enroll/PhotoConfirm'
import { PhotoStep } from '../components/enroll/PhotoStep'
import { StickyActionBar } from '../components/enroll/StickyActionBar'
import { DEFAULT_COUNTRY, DEFAULT_TYPE, TaxIdFields } from '../components/enroll/TaxIdFields'
import '../components/enroll/enroll.css'
import { useTranslation } from '../i18n'
import {
  applyDigitMask,
  SINGLE_INDIVIDUAL_TAX_ID_TYPE,
  TOGGLEABLE_TAX_ID_TYPE_ID,
} from '../lib/taxIdFields'

/** Same loose YYYY-MM-DD pattern as CoworkerEditForm's inline validation
 * (apps/admin/src/components/coworker/CoworkerEditForm.tsx) — fail-fast UX
 * only, the server re-validates regardless. Mobile phone validation uses the
 * real `isValidMobilePhone`/`toE164` helpers instead of a regex — see
 * `validateProfile` below. */
const BIRTH_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

type Phase = 'loading' | 'unverified' | 'not-linked' | 'error' | 'wizard'
type WizardStep = 'profile' | 'photo' | 'confirm' | 'finish' | 'done'

interface ProfileFormState {
  mobilePhone: string
  taxIdCountry: string
  taxIdType: string
  taxIdValue: string
  birthDate: string
}

type ProfileFormErrors = Partial<Record<keyof ProfileFormState, string>>

const emptyProfileForm: ProfileFormState = {
  mobilePhone: '',
  taxIdCountry: DEFAULT_COUNTRY,
  taxIdType: DEFAULT_TYPE,
  taxIdValue: '',
  birthDate: '',
}

/** Narrows `finalizeMyEnroll`'s `EnrollmentStatus` return to the two
 * terminal values it actually produces — mirrors
 * EnrollPage.tsx#enrollmentDoneStatus. */
function enrollmentDoneStatus(status: EnrollmentStatus): 'approved' | 'pending-approval' {
  return status === 'approved' ? 'approved' : 'pending-approval'
}

/** Determines the first wizard step to land on from the completeness
 * report's `missing` list. */
function firstStepFor(missing: MyEnrollmentCompleteness['missing']): WizardStep {
  if (missing.includes('mobilePhone') || missing.includes('taxIdValue')) return 'profile'
  if (missing.includes('facePhoto')) return 'photo'
  return 'finish'
}

const RESEND_COOLDOWN_SECONDS = 60

/** The API's hard gate: 403 + code EMAIL_NOT_VERIFIED. */
function isEmailNotVerified(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 403 &&
    (err.body as { code?: string } | null)?.code === 'EMAIL_NOT_VERIFIED'
  )
}

/** Authenticated self-service enrollment completion — route `/my/enroll`
 * (role 'user'). */
export function MyEnrollPage() {
  const { user, getAccessTokenSilently } = useAuth0()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const retake = useSearchParams()[0].get('retake') === '1'

  const [phase, setPhase] = useState<Phase>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [missing, setMissing] = useState<MyEnrollmentCompleteness['missing']>([])
  const [step, setStep] = useState<WizardStep>('profile')
  const [faceResult, setFaceResult] = useState<MyFaceStatus | null>(null)
  const [doneStatus, setDoneStatus] = useState<'approved' | 'pending-approval'>('pending-approval')

  // Only an explicit `false` from the ID token blocks up-front. When the claim
  // is simply absent we load and let the API (which is authoritative) decide.
  const emailKnownUnverified = user?.email_verified === false
  // Bumped by "I've verified" so the load re-runs even if the (stale) ID-token
  // claim still says false — the API then makes the final call.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (emailKnownUnverified && reloadKey === 0) {
      setPhase('unverified')
      return
    }

    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const result = await getMyEnrollment(token)
        if (cancelled) return
        setMissing(result.missing)
        setStep(retake ? 'photo' : firstStepFor(result.missing))
        setPhase('wizard')
      } catch (err) {
        if (cancelled) return
        if (isEmailNotVerified(err)) {
          setPhase('unverified')
        } else if (err instanceof ApiError && err.status === 404) {
          setPhase('not-linked')
        } else {
          setLoadError(err instanceof ApiError ? err.message : t('common.error'))
          setPhase('error')
        }
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `getAccessTokenSilently`
    // is stable in the real Auth0 provider; omitted so a test double
    // returning a fresh function per render can't spin this into a refetch loop.
  }, [emailKnownUnverified, reloadKey])

  // --- Unverified email: resend + re-check ---
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resending, setResending] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [resendNotice, setResendNotice] = useState<{
    kind: 'sent' | 'already' | 'error'
    message: string
  } | null>(null)

  useEffect(() => {
    if (resendCooldown <= 0) return
    const id = setTimeout(() => setResendCooldown((s) => s - 1), 1000)
    return () => clearTimeout(id)
  }, [resendCooldown])

  async function handleResend() {
    setResendNotice(null)
    setResending(true)
    try {
      const token = await getAccessTokenSilently()
      const result = await resendMyVerification(token)
      if (result.status === 'already_verified') {
        setResendNotice({ kind: 'already', message: t('myEnroll.unverified.alreadyVerified') })
      } else {
        setResendCooldown(RESEND_COOLDOWN_SECONDS)
        setResendNotice({
          kind: 'sent',
          message: t('myEnroll.unverified.sent', { email: user?.email ?? '' }),
        })
      }
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0
      if (status === 429) setResendCooldown(RESEND_COOLDOWN_SECONDS)
      const key =
        status === 409
          ? 'myEnroll.unverified.notSupported'
          : status === 429
            ? 'myEnroll.unverified.tooSoon'
            : status === 503
              ? 'myEnroll.unverified.unavailable'
              : 'myEnroll.unverified.error'
      setResendNotice({ kind: 'error', message: t(key) })
    } finally {
      setResending(false)
    }
  }

  async function handleReloadVerified() {
    setReloading(true)
    try {
      // The cached access token still carries the old claim — force a refresh.
      await getAccessTokenSilently({ cacheMode: 'off' })
    } catch {
      // Fall through: the reload below reports the real state.
    }
    setResendNotice(null)
    setPhase('loading')
    setReloadKey((k) => k + 1)
    setReloading(false)
  }

  // --- Step: profile ---
  const [profileForm, setProfileForm] = useState<ProfileFormState>(emptyProfileForm)
  const [profileErrors, setProfileErrors] = useState<ProfileFormErrors>({})
  const [profileServerError, setProfileServerError] = useState<string | null>(null)
  const [savingProfile, setSavingProfile] = useState(false)

  function setProfileField<K extends keyof ProfileFormState>(key: K, value: ProfileFormState[K]) {
    setProfileForm((prev) => {
      const next = { ...prev, [key]: value }
      if (key === 'taxIdCountry') {
        next.taxIdType = individualTaxIdTypesForCountry(value as string)[0]?.id ?? ''
      }
      return next
    })
  }

  /** Live-formats the mobile phone field as the user types — mirrors
   * EnrollPage.tsx#handlePhoneChange (see its comment for the
   * skip-formatting-while-deleting rationale and the empty-format
   * fallback). Also clears a previously-shown `invalidMobile` error as soon
   * as the value becomes valid, without requiring another submit. */
  function handlePhoneChange(raw: string) {
    const isDeleting = raw.length < profileForm.mobilePhone.length
    let next = raw
    if (!isDeleting) {
      next = formatPhoneAsYouType(raw, 'BR') || raw
    }
    setProfileField('mobilePhone', next)
    if (profileErrors.mobilePhone) {
      const e164 = toE164(next, 'BR')
      if (e164 && isValidMobilePhone(e164)) {
        setProfileErrors((prev) => ({ ...prev, mobilePhone: undefined }))
      }
    }
  }

  /** Live-formats the CPF field with its `mask` metadata as the user types —
   * mirrors EnrollPage.tsx#handleCpfChange. Submission still normalizes to
   * digits-only via `normalizeTaxId`, unchanged. */
  function handleCpfChange(raw: string) {
    const mask = SINGLE_INDIVIDUAL_TAX_ID_TYPE?.mask
    const isDeleting = raw.length < profileForm.taxIdValue.length
    const next = !isDeleting && mask ? applyDigitMask(raw, mask) : raw
    setProfileField('taxIdValue', next)
  }

  /** Switches the tax-id field to passport mode, clearing the (possibly
   * half-typed) CPF value — mirrors EnrollPage.tsx#switchToPassportMode.
   * `taxIdCountry` is left as-is. */
  function switchToPassportMode() {
    setProfileForm((prev) => ({ ...prev, taxIdType: TOGGLEABLE_TAX_ID_TYPE_ID, taxIdValue: '' }))
    setProfileErrors((prev) => ({ ...prev, taxIdValue: undefined }))
  }

  /** Inverse of `switchToPassportMode` — back to CPF, clearing the passport
   * value. */
  function switchToCpfMode() {
    setProfileForm((prev) => ({ ...prev, taxIdType: DEFAULT_TYPE, taxIdValue: '' }))
    setProfileErrors((prev) => ({ ...prev, taxIdValue: undefined }))
  }

  function validateProfile(): ProfileFormErrors {
    const next: ProfileFormErrors = {}
    if (!profileForm.mobilePhone.trim()) {
      next.mobilePhone = t('coworkerEdit.validation.mobilePhone')
    } else {
      const e164 = toE164(profileForm.mobilePhone.trim(), 'BR')
      if (!e164 || !isValidMobilePhone(e164)) {
        next.mobilePhone = t('enroll.validation.invalidMobile')
      }
    }
    const taxIdCheck = validateTaxId(profileForm.taxIdType, profileForm.taxIdValue)
    if (!taxIdCheck.ok) {
      next.taxIdValue = t(`enroll.validation.taxId.${taxIdCheck.reason ?? 'INVALID_FORMAT'}`)
    }
    if (profileForm.birthDate.trim() && !BIRTH_DATE_REGEX.test(profileForm.birthDate.trim())) {
      next.birthDate = t('coworkerEdit.validation.birthDate')
    }
    return next
  }

  async function handleProfileSubmit(e: React.FormEvent) {
    e.preventDefault()
    const validationErrors = validateProfile()
    setProfileErrors(validationErrors)
    if (Object.keys(validationErrors).length > 0) return

    setProfileServerError(null)
    setSavingProfile(true)
    try {
      const token = await getAccessTokenSilently()
      // `validateProfile()` above already confirmed this parses to a valid
      // mobile — the non-null assertion reflects that guarantee.
      const e164Phone = toE164(profileForm.mobilePhone.trim(), 'BR')!
      await putMyEnrollProfile(
        {
          mobilePhone: e164Phone,
          taxIdCountry: profileForm.taxIdCountry,
          taxIdType: profileForm.taxIdType,
          taxIdValue: normalizeTaxId(profileForm.taxIdType, profileForm.taxIdValue),
          birthDate: profileForm.birthDate.trim() || undefined,
        },
        token,
      )
      setStep(missing.includes('facePhoto') ? 'photo' : 'finish')
    } catch (err) {
      if (isEmailNotVerified(err)) {
        setPhase('unverified')
      } else if (err instanceof ApiError) {
        const code = (err.body as { code?: string } | null)?.code
        setProfileServerError(
          code === 'COMPANY_TAX_ID_NOT_ALLOWED'
            ? t('enroll.errors.COMPANY_TAX_ID_NOT_ALLOWED')
            : err.message,
        )
      } else {
        setProfileServerError(t('enroll.errors.generic'))
      }
    } finally {
      setSavingProfile(false)
    }
  }

  // --- Step: face capture + consent ---

  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null)
  const [photoBase64, setPhotoBase64] = useState<string | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [submittingFace, setSubmittingFace] = useState(false)

  function handleCapture(dataUrl: string) {
    setPhotoError(null)
    setPhotoBase64(dataUrl.split(',')[1] ?? '')
    setPhotoPreviewUrl(dataUrl)
    if (step === 'photo') {
      setStep('confirm')
    }
  }

  function handleRetake() {
    setPhotoBase64(null)
    setPhotoPreviewUrl(null)
    if (step === 'confirm') {
      setStep('photo')
    }
  }

  async function handleSubmitFace() {
    if (step !== 'confirm' || !photoBase64) return

    setPhotoError(null)
    setSubmittingFace(true)
    try {
      const token = await getAccessTokenSilently()
      if (retake) {
        // Replacing a photo: the door reader's answer comes back right away.
        const result = await putMyFacePhoto(token, photoBase64)
        setFaceResult(result.status)
        setStep('done')
      } else {
        await postMyEnrollFace({ photoBase64, consent: true }, token)
        setStep('finish')
      }
    } catch (err) {
      if (isEmailNotVerified(err)) setPhase('unverified')
      else setPhotoError(t('enroll.errors.generic'))
    } finally {
      setSubmittingFace(false)
    }
  }

  /** Reader rejected the retake: restart the selfie from scratch. */
  function handleTryAgain() {
    setFaceResult(null)
    handleRetake()
    setStep('photo')
  }

  // --- Step: finish ---
  const [finishError, setFinishError] = useState<string | null>(null)
  const [finalizing, setFinalizing] = useState(false)

  async function handleFinalize() {
    setFinishError(null)
    setFinalizing(true)
    try {
      const token = await getAccessTokenSilently()
      const result = await finalizeMyEnroll(token)
      setDoneStatus(enrollmentDoneStatus(result.status))
      setStep('done')
    } catch (err) {
      if (isEmailNotVerified(err)) {
        setPhase('unverified')
      } else {
        setFinishError(err instanceof ApiError ? err.message : t('enroll.errors.generic'))
      }
    } finally {
      setFinalizing(false)
    }
  }

  // Steps this enrollment will actually show, from the load-time `missing`
  // report (profile / photo+confirm are skipped when already complete).
  const stepSequence: Exclude<WizardStep, 'done'>[] = retake
    ? ['photo', 'confirm']
    : [
        ...(missing.includes('mobilePhone') || missing.includes('taxIdValue')
          ? (['profile'] as const)
          : []),
        ...(missing.includes('facePhoto') ? (['photo', 'confirm'] as const) : []),
        'finish',
      ]
  const stepNumber = Math.max(1, stepSequence.indexOf(step as Exclude<WizardStep, 'done'>) + 1)
  const stepNames: Record<Exclude<WizardStep, 'done'>, string> = {
    profile: t('myEnroll.step.profileTitle'),
    photo: retake ? t('myEnroll.retake.stepName') : t('myEnroll.step.photoTitle'),
    confirm: t('myEnroll.step.confirmTitle'),
    finish: t('myEnroll.finish.title'),
  }
  const stepName = step === 'done' ? '' : stepNames[step]

  return (
    <div className="pb-8">
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <Breadcrumbs.Link icon={<HouseIcon size={14} />} href="/">
              {t('nav.home')}
            </Breadcrumbs.Link>
            <Breadcrumbs.Separator />
            <Breadcrumbs.Current>{t('myEnroll.title')}</Breadcrumbs.Current>
          </Breadcrumbs>
        }
        title={t('myEnroll.title')}
        description={t('myEnroll.description')}
      />

      <div className="mx-auto flex w-full flex-col gap-4" style={{ maxWidth: '32rem' }}>
        <div className="flex flex-col gap-2">
          <Text as="p" size="sm">
            {t('myEnroll.identity.nameLabel')}: {user?.name}
          </Text>
          <Text as="p" size="sm">
            {t('myEnroll.identity.emailLabel')}: {user?.email}
          </Text>
        </div>

        {phase === 'unverified' && (
          <Banner variant="alert">
            <Text as="p" variant="heading3">
              {t('myEnroll.identity.unverifiedTitle')}
            </Text>
            <Text as="p">{t('myEnroll.identity.unverifiedMessage')}</Text>
          </Banner>
        )}

        {phase === 'unverified' && (
          <div className="flex flex-col gap-3">
            {reloadKey > 0 && !reloading && (
              <Banner variant="alert">{t('myEnroll.unverified.stillUnverified')}</Banner>
            )}
            {resendNotice && (
              <Banner variant={resendNotice.kind === 'error' ? 'error' : 'default'}>
                {resendNotice.message}
              </Banner>
            )}
            <Button
              variant="primary"
              loading={resending}
              disabled={resendCooldown > 0}
              onClick={() => void handleResend()}
              className="enroll-cta w-full"
            >
              {resendCooldown > 0
                ? t('myEnroll.unverified.resendWait', { seconds: String(resendCooldown) })
                : t('myEnroll.unverified.resend')}
            </Button>
            <Button
              variant="secondary"
              loading={reloading}
              onClick={() => void handleReloadVerified()}
              className="enroll-cta w-full"
            >
              {t('myEnroll.unverified.reload')}
            </Button>
          </div>
        )}

        {phase === 'loading' && (
          <div className="flex items-center justify-center py-16">
            <Loader size="lg" />
          </div>
        )}

        {phase === 'not-linked' && (
          <Empty
            icon={<UserIcon size={48} className="text-kumo-inactive" />}
            title={t('myProfile.notLinkedTitle')}
            description={t('myProfile.notLinkedDescription')}
          />
        )}

        {phase === 'error' && (
          <Banner variant="error">
            {t('common.errorPrefix')} {loadError}
          </Banner>
        )}

        {phase === 'wizard' && step !== 'done' && (
          <EnrollProgress step={stepNumber} total={stepSequence.length} name={stepName} />
        )}

        {phase === 'wizard' && step === 'profile' && (
          <form onSubmit={(e) => void handleProfileSubmit(e)} className="flex flex-col gap-4">
            {profileServerError && <Banner variant="error">{profileServerError}</Banner>}

            <Input
              className="enroll-input"
              label={t('enroll.fields.mobilePhone')}
              type="tel"
              value={profileForm.mobilePhone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              error={profileErrors.mobilePhone}
              description={t('enroll.fields.mobilePhoneHint')}
              autoComplete="tel"
              inputMode="tel"
              enterKeyHint="next"
              required
            />

            <TaxIdFields
              taxIdCountry={profileForm.taxIdCountry}
              taxIdType={profileForm.taxIdType}
              taxIdValue={profileForm.taxIdValue}
              error={profileErrors.taxIdValue}
              onCpfChange={handleCpfChange}
              onValueChange={(v) => setProfileField('taxIdValue', v)}
              onCountryChange={(v) => setProfileField('taxIdCountry', v)}
              onTypeChange={(v) => setProfileField('taxIdType', v)}
              onNoCpfChange={(noCpf) => (noCpf ? switchToPassportMode() : switchToCpfMode())}
            />

            <Input
              className="enroll-input"
              label={t('enroll.fields.birthDate')}
              type="date"
              value={profileForm.birthDate}
              onChange={(e) => setProfileField('birthDate', e.target.value)}
              error={profileErrors.birthDate}
              autoComplete="bday"
              enterKeyHint="done"
            />

            <StickyActionBar>
              <Button
                variant="primary"
                type="submit"
                loading={savingProfile}
                className="enroll-cta w-full"
              >
                {t('myEnroll.profile.submit')}
              </Button>
            </StickyActionBar>
          </form>
        )}

        {phase === 'wizard' && step === 'photo' && (
          <PhotoStep error={photoError} onCapture={handleCapture} />
        )}

        {phase === 'wizard' && step === 'confirm' && (
          <PhotoConfirm
            error={photoError}
            previewUrl={photoPreviewUrl}
            onRetake={handleRetake}
            onSubmit={() => void handleSubmitFace()}
            submitting={submittingFace}
            canSubmit={!!photoBase64}
          />
        )}

        {phase === 'wizard' && step === 'finish' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Text variant="heading3" as="h2">
                {t('myEnroll.finish.title')}
              </Text>
              <Text>{t('myEnroll.finish.description')}</Text>
            </div>

            {finishError && <Banner variant="error">{finishError}</Banner>}

            <StickyActionBar>
              <Button
                variant="primary"
                loading={finalizing}
                onClick={() => void handleFinalize()}
                className="enroll-cta w-full"
              >
                {t('myEnroll.finish.button')}
              </Button>
            </StickyActionBar>
          </div>
        )}

        {phase === 'wizard' && step === 'done' && retake && faceResult === 'retake' && (
          <div data-testid="retake-rejected" className="flex flex-col gap-4">
            <Banner variant="error">
              <Text as="p" variant="heading3">
                {t('myEnroll.retake.rejectedTitle')}
              </Text>
              <Text as="p">{t('myEnroll.retake.rejectedMessage')}</Text>
            </Banner>
            <Button variant="primary" onClick={handleTryAgain} className="enroll-cta w-full">
              {t('myEnroll.retake.tryAgain')}
            </Button>
          </div>
        )}

        {phase === 'wizard' && step === 'done' && retake && faceResult !== 'retake' && (
          <div
            data-testid="retake-done"
            data-result={faceResult}
            className="flex flex-col items-center gap-4 py-4 text-center"
          >
            {faceResult === 'ok' ? (
              <CheckCircleIcon size={56} weight="fill" className="text-kumo-success" />
            ) : (
              <HourglassMediumIcon size={56} className="text-kumo-warning" />
            )}
            <Text variant="heading3" as="h2">
              {t(
                faceResult === 'ok'
                  ? 'myEnroll.retake.okTitle'
                  : faceResult === 'duplicate'
                    ? 'myEnroll.retake.duplicateTitle'
                    : 'myEnroll.retake.doneTitle',
              )}
            </Text>
            {faceResult === 'duplicate' && (
              <Text variant="secondary">{t('myEnroll.retake.duplicateMessage')}</Text>
            )}
            <Button variant="primary" onClick={() => navigate('/')} className="enroll-cta w-full">
              {t('myEnroll.done.home')}
            </Button>
          </div>
        )}

        {phase === 'wizard' && step === 'done' && !retake && (
          <EnrollDone
            status={doneStatus}
            actionLabel={t('myEnroll.done.home')}
            onAction={() => navigate('/')}
          />
        )}
      </div>
    </div>
  )
}
